/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, X, Menu } from 'lucide-react';
import './NavBar.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function NavBar() {
  const location = useLocation();
  const path = location.pathname;
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPw, setLoginPw] = useState('');
  const [loginErr, setLoginErr] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const checkAuth = useCallback(async () => {
    if (sessionStorage.getItem('spring_admin_token')) {
      setIsAdmin(true);
    }
    const token = localStorage.getItem('spring_token');
    if (!token) {
      setUser(null);
      if (!sessionStorage.getItem('spring_admin_token')) setIsAdmin(false);
      return;
    }
    try {
      const res = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUser({ name: data.name, email: data.email, is_admin: data.is_admin });
        if (data.is_admin) setIsAdmin(true);
      } else {
        localStorage.removeItem('spring_token');
        setUser(null);
        if (!sessionStorage.getItem('spring_admin_token')) setIsAdmin(false);
      }
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    window.addEventListener('admin-auth-change', checkAuth);
    window.addEventListener('spring-auth-change', checkAuth);
    return () => {
      window.removeEventListener('admin-auth-change', checkAuth);
      window.removeEventListener('spring-auth-change', checkAuth);
    };
  }, [path, checkAuth]);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [path]);

  const handleLogin = async () => {
    setLoginLoading(true);
    setLoginErr('');
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPw }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invalid credentials');
      localStorage.setItem('spring_token', data.token);
      setShowLogin(false);
      setLoginEmail('');
      setLoginPw('');
      setLoginErr('');
      checkAuth();
      window.dispatchEvent(new Event('spring-auth-change'));
    } catch (e) {
      setLoginErr(e.message);
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('spring_token');
    sessionStorage.removeItem('spring_admin_token');
    setUser(null);
    setIsAdmin(false);
    window.dispatchEvent(new Event('spring-auth-change'));
    window.dispatchEvent(new Event('admin-auth-change'));
  };

  const [atTop, setAtTop] = useState(true);
  useEffect(() => {
    const onScroll = () => setAtTop(window.scrollY < 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [path]);
  // Transparent over the home hero only; solid as soon as the page scrolls
  const isHome = path === '/' && atTop;
  const darkClass = isHome ? 'on-dark' : '';
  const isActive = (p) => path === p ? `nav-link active ${darkClass}` : `nav-link ${darkClass}`;

  const navLinks = (
    <>
      <Link to="/" className={isActive('/')} data-testid="nav-home" onClick={() => setMobileOpen(false)}>Home</Link>
      <Link to="/about" className={isActive('/about')} data-testid="nav-about" onClick={() => setMobileOpen(false)}>About</Link>
      <Link to="/assessment" className={isActive('/assessment')} data-testid="nav-assessment" onClick={() => setMobileOpen(false)}>Assessment</Link>
      <Link to="/pathways" className={isActive('/pathways')} data-testid="nav-pathways" onClick={() => setMobileOpen(false)}>Pathways</Link>
      {user ? (
        <button className={`nav-user-btn ${darkClass}`} onClick={() => { handleLogout(); setMobileOpen(false); }}
          data-testid="nav-logout-btn" title={`Signed in as ${user.email}`}>
          <span className="nav-user-name">{user.name || user.email.split('@')[0]}</span>
          <LogOut size={14} />
        </button>
      ) : (
        <button className={`nav-login-btn ${darkClass}`} onClick={() => { setShowLogin(true); setMobileOpen(false); }}
          data-testid="nav-login-btn">
          Log in
        </button>
      )}
      <Link to="/admin" className={`nav-login-btn ${darkClass}`}
        style={{ textDecoration: 'none', textAlign: 'center' }}
        data-testid="nav-admin-link" onClick={() => setMobileOpen(false)}>
        Admin
      </Link>
    </>
  );

  return (
    <>
      <nav className={`spring-nav ${path === '/' ? 'spring-nav-overlay' : ''} ${isHome ? 'spring-nav-transparent' : ''}`} data-testid="spring-navbar">
        <div className="spring-nav-inner">
          <Link to="/" className={`spring-nav-logo ${isHome ? 'on-hero' : ''}`} data-testid="nav-home-link">
            <img
              src={isHome ? '/spring-logo-white-notagline.webp' : '/spring-logo-colour-notagline.webp'}
              alt="SPRING"
            />
          </Link>

          {/* Desktop links */}
          <div className="spring-nav-links spring-nav-desktop">
            {navLinks}
          </div>

          {/* Mobile hamburger */}
          <button className={`spring-nav-hamburger ${darkClass}`} onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="nav-hamburger" aria-label="Menu">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="spring-nav-mobile-drawer" data-testid="nav-mobile-drawer">
          <div className="spring-nav-mobile-links">
            {navLinks}
          </div>
        </div>
      )}

      {/* Login modal */}
      {showLogin && (
        <div className="nav-login-overlay" onClick={() => setShowLogin(false)} data-testid="nav-login-overlay">
          <div className="nav-login-modal" onClick={e => e.stopPropagation()} data-testid="nav-login-modal">
            <button className="nav-login-close" onClick={() => setShowLogin(false)} data-testid="nav-login-close">
              <X size={18} />
            </button>
            <h2>log in</h2>
            <div className="nav-login-field">
              <label>Email</label>
              <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
                placeholder="your.email@example.com" data-testid="nav-login-email" />
            </div>
            <div className="nav-login-field">
              <label>Password</label>
              <input type="password" value={loginPw} onChange={e => setLoginPw(e.target.value)}
                placeholder="Password" data-testid="nav-login-password"
                onKeyDown={e => e.key === 'Enter' && handleLogin()} />
            </div>
            {loginErr && <p className="nav-login-err" data-testid="nav-login-error">{loginErr}</p>}
            <button className="nav-login-submit" onClick={handleLogin} disabled={loginLoading || !loginEmail || !loginPw}
              data-testid="nav-login-submit">
              {loginLoading ? 'Signing in...' : 'Submit'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
